import { AGENT_PROCEDURE_VERSIONS_ACTION_CONSTANTS as CONSTANTS } from '../actions/agent_procedure_versions_actions';
import { AOPVersion, AOPVersionSummary } from '../types/AOPTypes';

/**
 * Published versions, which are immutable once cut, so nothing here is ever
 * invalidated — a version that has been read stays read for the session.
 *
 * `versions` is keyed by `procedureId:version` rather than nested, so a lookup
 * is a single bracket access. Read it with bracket access, never through
 * `nullSafeGet` — the path would be split on the dot in a uuid-free key but the
 * composite key is not a path.
 */
export interface AgentProcedureVersionsState {
	summaries: { [procedureId: string]: AOPVersionSummary[] };
	/** How many versions a procedure has, which is not how many are held. */
	counts: { [procedureId: string]: number };
	/**
	 * How many positions of the history have been read, which is not how many
	 * rows are held either — overlapping pages are merged away.
	 *
	 * It lives here rather than in the screen because the screen cannot see
	 * which kind of read happened: publishing rereads the first page, and an
	 * offset left over from paging would then skip everything between.
	 */
	offsets: { [procedureId: string]: number };
	versions: { [key: string]: AOPVersion };
	fetching: boolean;
	errorMessage: string | null;
}

export const versionKey = (procedureId: string, version: number): string =>
	`${procedureId}:${version}`;

const initialState: AgentProcedureVersionsState = {
	summaries: {},
	counts: {},
	offsets: {},
	versions: {},
	fetching: false,
	errorMessage: null,
};

export default (state = initialState, action): AgentProcedureVersionsState => {
	switch (action.type) {
		case CONSTANTS.fetchStart:
			return { ...state, fetching: true, errorMessage: null };

		case CONSTANTS.fetchListSuccess: {
			/*
			  A later page adds to what is held; a fresh read replaces it. Appending
			  by version rather than by concatenation, because a version published
			  while somebody is paging shifts every offset by one and would
			  otherwise arrive twice.
			*/
			const held = action.append ? state.summaries[action.procedureId] || [] : [];
			const known = new Set(held.map((summary) => summary.version));
			const merged = held.concat(action.summaries.filter((summary) => !known.has(summary.version)));

			// A fresh read starts the count again; a later page moves it on by the
			// positions that page covered, not by the rows that survived the merge.
			const read = action.append
				? Math.max(state.offsets[action.procedureId] || 0, action.offset + action.summaries.length)
				: action.summaries.length;

			return {
				...state,
				fetching: false,
				summaries: { ...state.summaries, [action.procedureId]: merged },
				counts: { ...state.counts, [action.procedureId]: action.count },
				offsets: { ...state.offsets, [action.procedureId]: read },
			};
		}

		case CONSTANTS.fetchOneSuccess:
			return {
				...state,
				fetching: false,
				versions: {
					...state.versions,
					[versionKey(action.procedureId, action.version.version)]: action.version,
				},
			};

		case CONSTANTS.fetchError:
			return { ...state, fetching: false, errorMessage: action.errorMessage };

		default:
			return state;
	}
};
